import { useEffect, useMemo, useState } from "react";
import { Palmtree, CheckCircle2, UserCheck } from "lucide-react";
import api from "@/lib/api";
import { DIVISION_COLOR } from "@/lib/constants";

// Reusable "Karyawan Cuti Hari Ini" widget — used on Dashboard and Team page.
export default function OnLeaveWidget({ testid = "section-on-leave-today" }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/leave-requests").then(({ data }) => setRows(data || [])).catch(() => setRows([]));
  }, []);

  const onLeaveToday = useMemo(() => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const ts = t.toISOString().slice(0, 10);
    return (rows || []).filter((lr) => {
      if (lr.status !== "approved") return false;
      const s = (lr.start_date || "").slice(0, 10);
      const e = (lr.end_date || lr.start_date || "").slice(0, 10);
      return s && e && s <= ts && ts <= e;
    }).sort((a, b) => (a.user_name || "").localeCompare(b.user_name || ""));
  }, [rows]);

  return (
    <section className="bg-white border border-slate-200" data-testid={testid}>
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-cyan-50">
        <div className="flex items-center gap-2">
          <Palmtree size={16} className="text-cyan-700" />
          <h2 className="font-display text-lg tracking-tight text-[#0B1D3A]">Karyawan Cuti Hari Ini</h2>
          <span data-testid="on-leave-count" className={`px-2 py-0.5 text-[10px] font-bold tabular-nums ${onLeaveToday.length > 0 ? "bg-cyan-600 text-white" : "bg-slate-200 text-slate-500"}`}>{onLeaveToday.length}</span>
        </div>
        <span className="text-[10px] text-slate-500 italic">{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
      </header>
      {onLeaveToday.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500">
          <CheckCircle2 size={22} className="mx-auto text-emerald-500 mb-1" />
          Tidak ada karyawan yang sedang cuti hari ini.
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {onLeaveToday.map((lr) => {
            const s = new Date(lr.start_date);
            const e = new Date(lr.end_date || lr.start_date);
            const daysRemaining = Math.max(0, Math.ceil((e.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
            return (
              <div key={lr.id} data-testid={`on-leave-row-${lr.id}`} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
                <div className="w-9 h-9 bg-cyan-100 text-cyan-800 flex items-center justify-center font-display font-semibold text-xs shrink-0">
                  {(lr.user_name || "?").split(" ").slice(0, 2).map((n) => n[0]).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0B1D3A] truncate">{lr.user_name}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[lr.user_division] || "bg-slate-100"}`}>{lr.user_division}</span>
                    <span className="uppercase tracking-wider font-semibold text-cyan-700">{lr.leave_type}</span>
                    <span className="tabular-nums text-slate-400">
                      {s.toLocaleDateString("id-ID", { day: "numeric", month: "short" })} – {e.toLocaleDateString("id-ID", { day: "numeric", month: "short" })}
                    </span>
                    <span className="tabular-nums text-slate-400">· sisa {daysRemaining} hari</span>
                  </div>
                  {lr.delegate_name ? (
                    <div className="text-[11px] mt-1 flex items-center gap-1.5 text-emerald-700" data-testid={`on-leave-delegate-${lr.id}`}>
                      <UserCheck size={11} className="text-emerald-600" />
                      <span className="text-slate-500">Tugas didelegasikan ke:</span>
                      <span className="font-semibold text-[#0B1D3A]">{lr.delegate_name}</span>
                      {lr.delegate_division && <span className={`px-1 py-0 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[lr.delegate_division] || "bg-slate-100"}`}>{lr.delegate_division}</span>}
                      <span className={`px-1 py-0 text-[9px] uppercase tracking-wider border ${
                        lr.delegate_status === "approved" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : lr.delegate_status === "rejected" ? "border-rose-300 bg-rose-50 text-rose-700"
                        : "border-amber-300 bg-amber-50 text-amber-700"
                      }`}>
                        {lr.delegate_status === "approved" ? "Diterima" : lr.delegate_status === "rejected" ? "Ditolak" : "Menunggu"}
                      </span>
                    </div>
                  ) : (
                    <div className="text-[11px] mt-1 text-slate-400 italic" data-testid={`on-leave-no-delegate-${lr.id}`}>Tidak ada delegasi tugas untuk cuti ini</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
